import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { Calendar, CalendarApi, EventInput } from 'fullcalendar';
import { BehaviorSubject, of } from 'rxjs';
import { isSameDay } from 'date-fns';
import { FormDataService } from './form-data.service';

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  private readonly formDataService = inject(FormDataService);

  _api: BehaviorSubject<CalendarApi | undefined> = new BehaviorSubject<
    CalendarApi | undefined
  >(undefined);
  _calendar: BehaviorSubject<Calendar | undefined> = new BehaviorSubject<
    Calendar | undefined
  >(undefined);

  /** Events explicitly hidden by the user (persists only in-memory; cleared on reload). */
  readonly hiddenEvents: WritableSignal<EventInput[]> = signal([]);

  public set api(api: CalendarApi) {
    this._api.next(api);
  }

  public get api(): BehaviorSubject<CalendarApi | undefined> {
    return this._api;
  }

  public set calendar(calendar: Calendar) {
    this._calendar.next(calendar);
  }

  public get calendar(): BehaviorSubject<Calendar | undefined> {
    return this._calendar;
  }

  hideSuggestion(event: EventInput): void {
    this.hiddenEvents.update((all) => [...all, event]);
  }

  unhideEvents(): void {
    const currentDate = this.api.value?.getDate();
    if (currentDate) {
      this.hiddenEvents.update((events) => events.filter((e) => !isSameDay(currentDate, e.start as Date)));
    }
  }

  syncToState() {
    if (this.api.value) {
      const events = this.api.value.getEvents();
      // Don't overwrite the store with an empty list — FullCalendar fires eventsSet
      // on initial render before events are loaded, which would erase persisted state.
      if (events.length === 0) return of(null);

      // Outlook calendar events (type='calendar') are always re-fetched from
      // Microsoft Graph and should not be persisted — they'd show as stale
      // "local" entries on the next load. CW time entries (type='cw') ARE
      // persisted so ReviewComponent can display and edit them.
      // Exception: calendar events that were explicitly selected from the
      // suggestions panel or manually moved/resized by the user ARE persisted
      // so they survive a page reload until submitted.
      const currentDate = this.api.value.getDate();

      const persistableEvents = events.filter(
        (e) =>
          // Only persist events that actually belong to the currently displayed date.
          // During a date transition, FullCalendar may still be showing stale events
          // from the previous date while getDate() has already advanced — filtering
          // by date here prevents those stale events from being saved under the wrong day.
          isSameDay(e.start as Date, currentDate) &&
          (e.extendedProps?.['type'] !== 'calendar' ||
            e.extendedProps?.['source'] === 'selected' ||
            e.extendedProps?.['userModified'] === true),
      );

      // Preserve events from other days — only replace today's slice.
      const otherDaysEvents = this.formDataService
        .events()
        .filter((e) => !isSameDay(e.start as Date, currentDate));

      this.formDataService.setEvents([
        ...otherDaysEvents,
        ...persistableEvents.map((e) => ({
          id: e.id,
          title: e.title,
          start: e.start as Date,
          end: e.end as Date,
          color: e.color,
          extendedProps: e.extendedProps,
        })),
      ]);
    }
    return of(null);
  }
}
